import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { addNotificationSMSlFormControlToFormGroup } from '@app/service-request-payment-input/presenter/service-request-payment-input-pres.form';
import { concatenateTravelerNames, isEmptyArray, isStringEmpty, stringEmptyDefault } from '@common/helpers';
import { SMS_DESTINATION } from '@common/helpers/constants/service-request-payment-input/service-request-payment-input.constants';
import { PhoneNumberCountryType } from '@common/interfaces';
import { PassengerSMSInformation } from '@common/interfaces/service-request-payment-input/service-request-payment-input.interface';
import { ServiceRequestPaymentInputService } from '@common/services/service-request-payment-input/service-request-payment-input-service';
import { SupportComponent } from '@lib/components/support-class';
import { AswValidators } from '@lib/helpers';
import { CommonLibService } from '@lib/services';
import { Subscription } from 'rxjs';
import { GetOrderResponseData, Traveler } from 'src/sdk-servicing';

@Component({
  selector: 'asw-service-request-payment-input-notification-sms',
  templateUrl: './service-request-payment-input-notification-sms.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceRequestPaymentInputNotificationSmsComponent extends SupportComponent {
  @Input() traveler!: Traveler;

  @Input() pnrData!: GetOrderResponseData;

  private _subscriptions: Subscription = new Subscription();
  // [8.11] 搭乗者SMS送信先
  public isDisplayPassengerSmsDestination: boolean = false;

  // [8.12] 搭乗者別SMS情報入力エリア
  public isDisplayPassengerSmsInputArea: boolean = false;

  public isPassengerSMSInformationsHasMoreThanTwo: boolean = false;

  passengerSMSInformations: Array<PassengerSMSInformation> = [];

  public phonesAsLabel: Array<{ countryName: string; phoneNumber: string }> = [];

  get paymentInputFG(): FormGroup {
    return this._serviceRequestPaymentInputService.paymentInputFG;
  }

  SMS_DESTINATION = SMS_DESTINATION;

  get id(): string {
    return `asw-service-request-payment-input-notification-sms-${this.traveler.id}`;
  }

  // [8.10] 搭乗者名
  get travelerName(): string {
    const travelerNames = this.traveler.names?.[0];
    return concatenateTravelerNames(travelerNames ?? {});
  }

  get phoneNumberCountryList(): Array<PhoneNumberCountryType> {
    return this._serviceRequestPaymentInputService.phoneNumberCountryList;
  }

  get countryMap(): Map<string, string> {
    return this._serviceRequestPaymentInputService.countryMap;
  }

  constructor(
    private readonly _common: CommonLibService,
    private readonly _serviceRequestPaymentInputService: ServiceRequestPaymentInputService,
    private readonly _changeDetectorRef: ChangeDetectorRef
  ) {
    super(_common);
  }

  init(): void {
    this._subscriptions = this._serviceRequestPaymentInputService.checkValid$.subscribe((checkValid) => {
      if (checkValid) {
        this._changeDetectorRef.detectChanges();
      }
    });

    // [8.11] 搭乗者SMS送信先
    this.isDisplayPassengerSmsDestination = this.onDisplayTravelerSMSDestination();

    // [8.12] 搭乗者別SMS情報入力エリア
    this.isDisplayPassengerSmsInputArea = this.onDisplayPassengerSmsInput();

    // [8.13] 搭乗者SMS国選択
    if (this.isDisplayPassengerSmsInputArea) {
      this.onHandleValidator();
      if (this.passengerSMSInformations?.length === 1) {
        // add to check item 8.12 can edit or not see @cardinal.service
        const travelerIdsCanEdit = this._serviceRequestPaymentInputService.travelerCanEditInputNotificationSMS$.value;
        travelerIdsCanEdit.add(this.traveler.id!);
        this._serviceRequestPaymentInputService.travelerCanEditInputNotificationSMS$.next(travelerIdsCanEdit);
      }
    }
  }

  onDisplayPassengerSmsInput(): boolean {
    if (this.passengerSMSInformations?.length >= 2) {
      // ※搭乗者SMS情報リスト≧2件の場合、以降の項目をラベルで表示する。
      this.phonesAsLabel = this.passengerSMSInformations.map((smsCountry) => ({
        countryName: this.getCountryName(smsCountry.countryPhoneExtension ?? '') ?? '',
        phoneNumber: `+${smsCountry?.countryPhoneExtension ?? ''} ${smsCountry?.number ?? ''}`,
      }));
    }

    if (isEmptyArray(this.passengerSMSInformations)) {
      // ※搭乗者SMS国選択がプルダウン、かつSMS情報リストが0件、かつPNR情報取得API応答.data.contacts.representative.phones[0]が存在する場合、PNR情報取得API応答.data.contacts.representative.phones[0].countryPhoneExtensionをキー、イベントID(1)で作成した電話番号対象国(マップ)から当該国コード(2 レター)を取得する
      const firstPhoneRepresentative = this.pnrData?.contacts?.representative?.phones?.[0];
      const newSms = {
        number: stringEmptyDefault(firstPhoneRepresentative?.number),
        countryPhoneExtension: stringEmptyDefault(firstPhoneRepresentative?.countryPhoneExtension),
      };
      // 1.NHグループ運航でアメリカ行き便有無=true、かつSMS情報リストが0件の場合、以降の項目に活性とし、未入力で初期化する。
      if (this._serviceRequestPaymentInputService.isNhGroupOperatedAndArrivalUs || firstPhoneRepresentative) {
        this.passengerSMSInformations = [newSms];
      }
    }

    // 画面.申込内容連絡先情報.<当該申込搭乗者ID>.搭乗情報SMS送信先にて「個人に送る」が選択されている または NHグループ運航でアメリカ行き便有無=trueの場合、表示する
    const isSendToDestination =
      this.paymentInputFG.get(`smsDestination_${this.traveler.id ?? ''}`)?.value === SMS_DESTINATION.SEND_TO_INDIVIDUAL;
    return isSendToDestination || this._serviceRequestPaymentInputService.isNhGroupOperatedAndArrivalUs;
  }

  onDisplayTravelerSMSDestination(): boolean {
    let isShow = false;
    const isNhGroupOperatedAndArrivalUs = this._serviceRequestPaymentInputService.isNhGroupOperatedAndArrivalUs;
    if (!this.pnrData?.orderType?.isGroupBooking) {
      isShow = true;
    }

    const condition1 = this.pnrData?.specialServiceRequests?.some(
      (specialServiceRequest) =>
        specialServiceRequest.code === 'CTCR' && specialServiceRequest.travelerIds?.includes(this.traveler?.id!)
    );

    // [8.11.1] PNR情報取得API応答.data.specialServiceRequests内に、codeが""CTCR""かつtravelerIdsに当該travelers.idが含まれる。
    // [8.11.2] NHグループ運航でアメリカ行き便有無=trueである。
    if (condition1 || isNhGroupOperatedAndArrivalUs) {
      isShow = false;
    }

    // [8.11.1.1]PNR情報取得API応答.data.contacts.travelers.<当該travelers.id>を連絡先情報とする。
    const contactTraveler = this.pnrData?.contacts?.[this.traveler.id!];

    // [8.11.1.2] 連絡先情報を引数として、「当画面共通処理.搭乗者SMS国選択の初期化処理」を実施して、返却値の搭乗者SMS情報リストを取得する。
    this.passengerSMSInformations = this._serviceRequestPaymentInputService.handleCommonNo6(contactTraveler);

    // [8.11.2].当ラジオボタンが表示する場合、初期選択は以下の通りに設定する。
    if (isShow) {
      // [8.11.2.1] 搭乗者SMS情報リストが1件の場合、「個人に送る」ラジオボタンを選択する。
      if (this.passengerSMSInformations.length === 1) {
        // (14.1.1.1.18) 搭乗情報SMS送信先
        this.addSmsDestinationFormControl();
        // [8.11.3.4] 搭乗者SMS情報リストが2件以上の場合、当ラジオボタンを非表示とする。
      } else if (this.passengerSMSInformations.length > 1) {
        this.isPassengerSMSInformationsHasMoreThanTwo = true;
        // [8.11.3.5] 上記以外の場合、イベント(1)で「搭乗者SMS送信選択初期値=true、かつ「搭乗者SMS情報リストが1件以上」の場合、「個人に送る」ラジオボタンを選択する。
      } else if (
        this._serviceRequestPaymentInputService.smsTransmissionPreference === true &&
        this.passengerSMSInformations.length > 0
      ) {
        // (14.1.1.1.18) 搭乗情報SMS送信先
        this.addSmsDestinationFormControl();
      } else {
        // [8.11.3.6] 記以外の場合、「送信しない」ラジオボタンを選択する。
        const smsDestinationFC = new FormControl(SMS_DESTINATION.DO_NOT_SEND, []);
        this.paymentInputFG.addControl(`smsDestination_${this.traveler.id}`, smsDestinationFC);
      }
    }
    return isShow;
  }

  addSmsDestinationFormControl() {
    this.paymentInputFG.addControl(
      `smsDestination_${this.traveler.id!}`,
      new FormControl(SMS_DESTINATION.SEND_TO_INDIVIDUAL, [
        AswValidators.required({
          errorMsgId: 'E0002',
          params: {
            key: 0,
            value: 'heading.recipientOfSmsAboutBoardingForServiceRequest',
          },
        }),
      ])
    );
  }

  // (12)	搭乗者SMS送付先ラジオボタン切り替え処理
  onChangeSmsDestination(): void {
    // (12.1)	選択した搭乗者SMS送付先により、画面.搭乗者別SMS情報入力エリアを「表示内容」シートの「初期表示」の当該項目に従って表示する。
    this.isDisplayPassengerSmsInputArea =
      this.paymentInputFG.get(`smsDestination_${this.traveler.id}`)?.value === SMS_DESTINATION.SEND_TO_INDIVIDUAL;
    if (this.isDisplayPassengerSmsInputArea) {
      // add validation when select option1
      this.onHandleValidator();
    } else {
      // remove validation when select option2
      this.paymentInputFG.removeControl('numberCountry_' + this.traveler.id);
      this.paymentInputFG.removeControl('number_' + this.traveler.id);
    }
  }

  onHandleValidator() {
    // [8.13.1] 当該搭乗者SMS情報.国番号をキー、イベントID(1)で作成した電話番号対象国(マップ)から当該国コード(2 レター)を取得する。
    // [8.13.2] キー=国コード(2 レター)である項目を表示する。
    // 選択肢搭乗者SMS国選択の選択値はリスト定義の搭乗者SMS国選択を参照。
    let countryCode = this.countryMap.get(this.passengerSMSInformations?.[0].countryPhoneExtension!);
    // 「搭乗者SMS送信先」項目で取得した搭乗者SMS情報リストを繰り返し、当該搭乗者SMS情報.国番号≠""""(空欄)の場合、以降の項目の初期値を設定する。
    this._serviceRequestPaymentInputService.phoneCountryCode = isStringEmpty(countryCode)
      ? ''
      : `+${this.passengerSMSInformations?.[0].countryPhoneExtension}`;
    const phoneCountryExtension =
      this.countryMap.get(this.paymentInputFG.get(`numberCountry_${this.traveler.id}`)?.value ?? '') ?? '';
    addNotificationSMSlFormControlToFormGroup(
      this.paymentInputFG,
      this.traveler.id!,
      phoneCountryExtension,
      countryCode ?? '',
      this.passengerSMSInformations?.[0].number!
    );
  }
  // [8.14] 搭乗者SMSの国番号
  get phoneCountryCode() {
    return this._serviceRequestPaymentInputService.phoneCountryCode;
  }

  // (13)	搭乗者SMSの国番号選択プルダウン押下処理
  public updateCountryCode() {
    // (13.1)	画面.搭乗者SMSの国番号を「表示内容」シートの「初期表示」の当該項目に従って表示する。
    const phoneCountryExtension =
      this.countryMap.get(this.paymentInputFG.get(`numberCountry_${this.traveler.id}`)?.value ?? '') ?? '';
    this._serviceRequestPaymentInputService.phoneCountryCode = `+${phoneCountryExtension}`;
  }

  getCountryName(countryCode: string) {
    if (!countryCode) {
      return '';
    }

    let countryLetterCode = '';
    for (const [key, value] of this.countryMap) {
      if (value === countryCode) {
        countryLetterCode = key;
        break;
      }
    }

    const phoneNumberCountry = this.phoneNumberCountryList.find((item) => {
      return countryLetterCode === item.country2LetterCode;
    });

    return phoneNumberCountry?.countryName ?? '';
  }

  destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }

  reload(): void {
    /* override abstract reload function of base class */
  }
}
